import { useEffect, useState } from 'react';
import {Helmet} from "react-helmet";
import './App.css';

import { ThemeOptions, createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@emotion/react';
import { AppBar, Box, Button, Card, CardHeader, CssBaseline, Collapse, IconButton, IconButtonProps, TextField, Toolbar, Typography, CardContent, FormControl, MenuItem, Select, InputLabel, CircularProgress, Dialog, DialogTitle, DialogContent, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useSelector, useDispatch, useStore } from 'react-redux';
import { AppDispatch, RootState } from './app/store';
import { setPrimaryColor, setShopName, setFont, setShop, setShopId } from './features/shop/shopSlice';

import Palettes from './features/palette/palette';
import ColorSelector from './Color/Color';
import { fonts } from './consts/fonts';

import { auth, signIn, updateShop, getShop } from './firebase/firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { setPalettes } from './features/palette/paletteSlice';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }: any) => ({
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }: ExpandMoreProps) => !expand,
      style: {
        transform: 'rotate(0deg)',
      },
    },
    {
      props: ({ expand }: ExpandMoreProps) => !!expand,
      style: {
        transform: 'rotate(180deg)',
      },
    },
  ],
}));

function App() {
  const store = useStore();
  const shop = useSelector((state: RootState) => state.shop);
  const dispatch: AppDispatch = useDispatch();

  const [signInMessage, setSignInMessage] = useState("");
  const [showPalettes, setShowPalettes] = useState(true);
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const shopData = await getShop(user);
        dispatch(setShop(shopData.shop));
        dispatch(setPalettes(shopData.palettes))
      } else {
        setUser(undefined);
      }
      setLoading(false);
    })
  }, [dispatch])

  if (!user || loading) {
    return (
    <ThemeProvider theme={createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: '#1976d2',
        },
        secondary: {
          main: '#f50057',
        },
        background: {
          default: '#eeeeee',
        },
      },
    })}>
      <CssBaseline/>
      <AppBar position='sticky'>
        <Toolbar>
          <Typography variant='h6' component="div" sx={{flexGrow: 1, fontFamily: shop.font.fontFamily, fontSize: 36}}>Flower Quote Admin Page</Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{alignItems: 'center', width: '100%'}}>
        <Box sx={{maxWidth: '400px', margin: '0 auto', my: 2}}>
          <Card>
            <CardHeader
              title="Sign In"
            />
            <CardContent>
              <Box component='form' autoComplete='off' onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries((formData as any).entries());
                setLoading(true);
                signIn(formJson.email, formJson.password).then((success) => {
                  if (success) {
                    setSignInMessage("");
                  } else {
                    setSignInMessage("Was not able to sign in user. Make sure your email and password are correct")
                  }
                  setLoading(false);
                });
              }} sx={{width: '100%'}}>
                {
                  loading === true ?
                  <>
                    <Box sx={{ display: 'flex' }}>
                      <CircularProgress />
                    </Box>
                  </> :
                  <>
                    {signInMessage ?
                      <Alert sx={{marginBottom: 2}} severity='error'>{signInMessage}</Alert>:
                      <></>}
                    <TextField id='email' name='email' sx={{width: '100%'}} required label="Email Address" />
                    <TextField id='password' name='password' type='password' sx={{my: 2, width: '100%'}} required label="Password" />    
                    <Button variant='contained' type='submit'>Next</Button>
                  </>
                }
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
    )
  }

  const themeOptions: ThemeOptions = {
    palette: {
      mode: 'light',
      primary: {
        main: shop.theme.primaryColor,
      },
      secondary: {
        main: shop.theme.secondaryColor,
      },
      background: {
        default: '#eeeeee',
      },
    },
  };

  return (
    <ThemeProvider theme={createTheme(themeOptions)}>
      <Helmet>
        {shop.font.fontUrl ? <link href={shop.font.fontUrl} rel="stylesheet"/> : ""}
        <title>{shop.name}</title>
      </Helmet>
      <CssBaseline/>
      <AppBar position='sticky'>
        <Toolbar>
          <Typography variant='h6' component="div" sx={{flexGrow: 1, fontFamily: shop.font.fontFamily, fontSize: 36}}>{shop.name}</Typography>
          <Button sx={{mx: 2}} variant='contained' color='secondary' onClick={() => {
            const state = store.getState() as RootState;
            let shop = state.shop;
            let palettes = state.palettes;
            shop = structuredClone(shop);
            palettes = structuredClone(palettes)
            if (!shop.id) {
              // TODO(check to make sure that the id isn't already taken)
              const id = shop.name.toLowerCase().replaceAll(" ", "_");
              shop.id = id;
              dispatch(setShopId(id))
            };
            setSaving(true);
            updateShop(user, {shop: shop, palettes: palettes.palettes}).then(() => {
              setSaving(false);
            });
          }}>Save</Button>
          <Button variant='contained' color='warning' onClick={() => {signOut(auth)}}>Sign out</Button>
        </Toolbar>
      </AppBar>
      <Box sx={{alignItems: 'center', width: '100%', }}>
        <Box sx={{maxWidth: '1500px', width: '100%', margin: '0 auto', my: 2}}>
          <Card>
            <CardHeader
              title="Shop Information"
              subheader={<a target="_blank" rel="noopener noreferrer" href={`https://quotearose.com/?shopId=${shop.id}`}>{shop.id}</a>}
            />
            <CardContent>
              <Box style={{display: 'flex', alignItems: 'center'}}>
                <Typography>Shop Name:</Typography>
                <TextField sx={{mx:2}} required value={shop.name} onChange={(event) => dispatch(setShopName(event.target.value))}></TextField>
              </Box>
              <Box sx={{my: 2, display: 'flex', alignItems: 'center'}}>
                <Typography>Font:</Typography>
                <FormControl sx={{minWidth: '200px', mx: 2}}>
                  <InputLabel>Font Family</InputLabel>
                  <Select
                    id="font-family"
                    label='font-family'
                    variant='filled'
                    value={shop.font.fontFamily}
                    onChange={(event) => dispatch(setFont(event.target.value))}>
                    { fonts.map((font, idx) => {
                      return (
                        <MenuItem key={idx} value={font.fontFamily}>{font.fontFamily}</MenuItem>
                      )
                    }) }
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{my: 2, display: 'flex', alignItems: 'center'}}>
                <Typography sx={{marginRight: 2}}>Primary Shop Color:</Typography>
                <ColorSelector color={shop.theme.primaryColor} onChange={(color) => dispatch(setPrimaryColor(color.hex))}/>
              </Box>
              <Box>

              </Box>
            </CardContent>
          </Card>
          <Card sx={{my: 2}}>
            <CardHeader
              action={
                <ExpandMore
                  expand={showPalettes}
                  onClick={() => {setShowPalettes(!showPalettes)}}
                  aria-expanded={showPalettes}
                  aria-label="show more"
                >
                  <ExpandMoreIcon />
                </ExpandMore>
              }
              title="Palettes Offered"
            />
            <Collapse in={showPalettes} timeout="auto">
              <CardContent>
                <Palettes></Palettes>
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      </Box>
      <Dialog open={saving}>
        <DialogTitle>Saving Shop Information</DialogTitle>
        <DialogContent sx={{display: 'flex', justifyContent: 'center'}}>
          <CircularProgress sx={{height: '50px'}}></CircularProgress>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
